/**
 * S3-compatible object storage (MinIO) data layer — shared infra.
 *
 * Backend lives on the @mgmt control plane (`serviceApiV3`, base already
 * includes `/api/v3`). Every route is service-scoped under
 * `/services/:serviceId/storage` and answers the v3 envelope `{ data: T }`.
 * The connection (zone + decrypted credential) is resolved server-side from
 * the service id — no zone params or secrets ever cross the wire here.
 */
import type { AxiosResponse } from 'axios';
import { serviceApiV3 } from '@/api/client';

export { serviceApiV3 };

// ---- Envelope ------------------------------------------------------------

export interface DataEnvelope<T> {
  status?: number;
  message?: string;
  data: T;
}

/** Return the inner `data` of a v3 response (tolerant of a raw body). */
export function unwrap<T>(res: AxiosResponse<DataEnvelope<T> | T>): T {
  const body = res.data as unknown;
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as DataEnvelope<T>).data;
  }
  return body as T;
}

/** Pull a human-readable message from a v3/axios error, else `fallback`. */
export function s3ExtractError(e: unknown, fallback = 'Request failed'): string {
  if (e && typeof e === 'object') {
    const ax = e as {
      response?: { data?: { message?: string; error?: { message?: string } } };
      message?: string;
    };
    const msg = ax.response?.data?.message ?? ax.response?.data?.error?.message;
    if (msg) return msg;
    if (ax.message) return ax.message;
  }
  return fallback;
}

/** Base path for every storage call on one provisioned service. */
export function storageBase(serviceId: string): string {
  return `/services/${serviceId}/storage`;
}

// ---- Domain types (json field names match the Go DTOs) -------------------

/** One bucket as listed (`GET /storage/buckets`). */
export interface Bucket {
  name: string;
  creation_date?: string | null;
}

/** Per-bucket aggregate row inside the overview payload. */
export interface BucketUsage {
  name: string;
  size: number;
  objects: number;
}

/**
 * Account-wide usage roll-up (`GET /storage/overview`).
 *
 * `active_storage_bytes` / `objects` / `buckets` are the accurate figures pulled
 * from the Wasabi console and are what the UI should surface. `total_size` /
 * `total_objects` are the legacy walk-based estimates, kept for back-compat.
 * The `*_change` fields are an upstream flag/factor (not a percentage delta), so
 * they are intentionally omitted from the type — rendering them would mislead.
 */
export interface UsageOverview {
  buckets: number;
  total_size: number;
  total_objects: number;
  /** Accurate billed storage in bytes (Wasabi). */
  active_storage_bytes?: number;
  /** Bytes retained for deleted/versioned objects still billed. */
  deleted_storage_bytes?: number;
  /** Accurate object count (Wasabi). */
  objects?: number;
  /** Snapshot date of the accurate figures, e.g. "2026-06-29". */
  as_of_date?: string | null;
  /** Origin of the accurate figures ("wasabi" | ...). */
  source?: string | null;
  by_bucket: BucketUsage[];
}

/**
 * One daily point of a storage-utilization series. Shared by the account-wide
 * series (`GET /storage/usage`) and the per-bucket series
 * (`GET /storage/buckets/:bucket/utilization`); the trailing fields after
 * `billable_objects` are only populated by the per-bucket endpoint.
 */
export interface UsageSeriesPoint {
  ts: string;
  end_ts?: string;
  /** Billed padded storage — the value plotted. */
  bytes: number;
  raw_bytes?: number;
  deleted_bytes?: number;
  metadata_bytes?: number;
  billable_objects?: number;
  /** Per-bucket only: bytes attributed to orphaned parts. */
  orphaned_bytes?: number;
  /** Per-bucket only: billable count of deleted/versioned objects. */
  billable_deleted_objects?: number;
  /** Per-bucket only: Wasabi storage class, e.g. "Hot". */
  storage_class?: string;
}

/** Time-series storage utilization (`GET /storage/usage`). Empty on non-Wasabi. */
export interface UsageSeries {
  series: UsageSeriesPoint[];
}

/**
 * Account-wide reconciliation summary over a window (`GET /storage/business`).
 * `peak_stored_bytes` is the billed basis — company policy bills the SUM of each
 * bucket's own peak (so the headline ties to the per-bucket table); it exceeds
 * the account's single-day peak because a max isn't linear. `avg_stored_bytes`
 * is the summed average, for reference. `billable_objects` is the summed latest
 * in-window count (a stock).
 */
export interface StorageBusinessSummary {
  avg_stored_bytes: number;
  peak_stored_bytes: number;
  raw_bytes: number;
  deleted_bytes: number;
  metadata_bytes: number;
  billable_objects: number;
  /** peak_stored_bytes / 1024³ — the billed GiB figure shown against the invoice
   *  (datacentral bills storage volume in binary GiB, not decimal GB). */
  gib_month: number;
  /** Number of daily points that fell in the window (0 → empty period). */
  points: number;
}

/** One bucket's windowed reconciliation row inside the business payload. */
export interface StorageBucketUsageRow {
  bucket: string;
  avg_stored_bytes: number;
  peak_stored_bytes: number;
  billable_objects: number;
  deleted_bytes: number;
}

/**
 * Reconciliation ("đối soát") payload (`GET /storage/business?from=&to=`): the
 * billing-basis summary plus a per-bucket breakdown, sourced live from the Wasabi
 * console. Empty summary/buckets on a non-Wasabi (generic MinIO) service.
 */
export interface StorageBusiness {
  from: string;
  to: string;
  summary: StorageBusinessSummary;
  buckets: StorageBucketUsageRow[];
}

/** Versioning is reported (and set) as an S3 state, not a boolean, so that a
 * bucket that was once versioned then suspended is distinguishable from one
 * that never had it. */
export type VersioningState = 'Enabled' | 'Suspended';

/** Server access-logging config for a bucket (`.../logging`). Target fields are
 * only meaningful when `enabled` — an off bucket may report empty targets. */
export interface BucketLogging {
  enabled: boolean;
  target_bucket?: string | null;
  target_prefix?: string | null;
}

/** Object-lock retention modes. GOVERNANCE can be bypassed with a permission;
 * COMPLIANCE cannot be shortened or removed by anyone until it expires. */
export type ObjectLockMode = 'GOVERNANCE' | 'COMPLIANCE';

/** Default retention on an object-lock-enabled bucket (`.../object-lock`).
 * Exactly one of `days`/`years` carries the duration when `enabled`. */
export interface ObjectLockRetention {
  enabled: boolean;
  mode?: ObjectLockMode | null;
  days?: number | null;
  years?: number | null;
}

/** Best-effort bucket settings (`GET /storage/buckets/:bucket`). Each field is
 * read independently server-side, so any may be absent. */
/** A single bucket tag as returned by the settings read endpoint. */
export interface BucketTag {
  key: string;
  value: string;
}

export interface BucketSettings {
  versioning?: VersioningState | null;
  /** True when the "block all public access" guard is on. */
  public_access_block?: boolean | null;
  /** Object lock is fixed at creation — shown read-only. */
  object_lock?: boolean;
  /** Read shape: the backend returns tags as a {key,value} list (empty list when none). */
  tags?: BucketTag[] | null;
  policy?: string | null;
  logging?: BucketLogging | null;
  object_lock_detail?: ObjectLockRetention | null;
}

/** A stored object (leaf) in a listing page. */
export interface S3Object {
  key: string;
  size: number;
  etag?: string | null;
  last_modified?: string | null;
  storage_class?: string | null;
}

/** A "folder" prefix returned by the delimiter listing. */
export interface CommonPrefix {
  prefix: string;
}

/** One page of a delimited object listing (`GET .../objects`). */
export interface ObjectListing {
  common_prefixes: CommonPrefix[];
  contents: S3Object[];
  is_truncated: boolean;
  next_token?: string | null;
}

/** A versioned object entry (`GET .../objects/versions`). */
export interface ObjectVersion {
  key: string;
  version_id?: string | null;
  is_latest?: boolean;
  size: number;
  etag?: string | null;
  last_modified?: string | null;
  storage_class?: string | null;
}

/** Enabled/disabled state of an S3 access key. */
export type AccessKeyStatus = 'Active' | 'Inactive';

/** One S3 access key as listed (`GET /storage/access-keys`). */
export interface AccessKey {
  access_key_id: string;
  status: AccessKeyStatus;
  create_date?: string | null;
  user_name?: string | null;
}

/** Freshly-minted key — the secret is returned once, on create, and never again. */
export interface NewAccessKey {
  access_key_id: string;
  secret_access_key: string;
}

/** Presigned PUT target for a direct browser→S3 upload. */
export interface PresignUpload {
  url: string;
  method: 'PUT';
  headers?: Record<string, string> | null;
}

// ---- Query keys ----------------------------------------------------------

export const s3Keys = {
  all: ['s3'] as const,
  overview: (serviceId: string) => ['s3', serviceId, 'overview'] as const,
  usageSeries: (serviceId: string) => ['s3', serviceId, 'usage-series'] as const,
  business: (serviceId: string, from: string, to: string) =>
    ['s3', serviceId, 'business', from, to] as const,
  buckets: (serviceId: string) => ['s3', serviceId, 'buckets'] as const,
  bucket: (serviceId: string, bucket: string) =>
    ['s3', serviceId, 'bucket', bucket] as const,
  bucketUsage: (serviceId: string, bucket: string) =>
    ['s3', serviceId, 'bucket', bucket, 'usage'] as const,
  logging: (serviceId: string, bucket: string) =>
    ['s3', serviceId, 'bucket', bucket, 'logging'] as const,
  objectLock: (serviceId: string, bucket: string) =>
    ['s3', serviceId, 'bucket', bucket, 'object-lock'] as const,
  objects: (serviceId: string, bucket: string, prefix: string, marker: string) =>
    ['s3', serviceId, 'bucket', bucket, 'objects', prefix, marker] as const,
  versions: (serviceId: string, bucket: string, prefix: string) =>
    ['s3', serviceId, 'bucket', bucket, 'versions', prefix] as const,
  accessKeys: (serviceId: string) => ['s3', serviceId, 'access-keys'] as const,
  policies: (serviceId: string) => ['s3', serviceId, 'policies'] as const,
  policy: (serviceId: string, arn: string) => ['s3', serviceId, 'policy', arn] as const,
  // IAM directory — list caches keyed by service; detail caches also by entity name
  // so a user/group/role view never collides with another of the same service.
  iamUsers: (serviceId: string) => ['s3', serviceId, 'iam-users'] as const,
  iamUser: (serviceId: string, name: string) => ['s3', serviceId, 'iam-user', name] as const,
  iamGroups: (serviceId: string) => ['s3', serviceId, 'iam-groups'] as const,
  iamGroup: (serviceId: string, name: string) => ['s3', serviceId, 'iam-group', name] as const,
  iamRoles: (serviceId: string) => ['s3', serviceId, 'iam-roles'] as const,
  iamRole: (serviceId: string, name: string) => ['s3', serviceId, 'iam-role', name] as const,
} as const;
